import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  ReadDevicesDirectionEnum,
  ReadDevicesRequest,
  Role,
  StbGroupNameResponse,
  TagEnum,
  UserRequest,
} from '@openapi';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Modal,
  notification,
  Popover,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  InfoCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';

import { Device, DeviceFilterVersions, DeviceType, JobExportDevices, StbGroup } from '@services/types';
import api from '@services/api';
import { getUserRoleByRoles, selectSearchFilter } from '@services/utils';
import { Navigation } from '@constants/Navigation';

import styles from './styles.module.scss';
import useQuery from '@services/useQuery';
import { AppContext } from '@layouts/AppRouter';

const { Column } = Table;
const { Option } = Select;
const { Panel } = Collapse;

const notConnected = [
  { days: 1, value: '1 day ago' },
  { days: 2, value: '2 day ago' },
  { days: 3, value: '3 day ago' },
  { days: 4, value: '4 day ago' },
  { days: 5, value: '5 day ago' },
  { days: 6, value: '6 day ago' },
  { days: 7, value: '1 week ago' },
  { days: 14, value: '2 weeks ago' },
  { days: 21, value: '3 weeks ago' },
  { days: 30, value: '1 month ago' },
  { days: 60, value: '2 months ago' },
  { days: 90, value: '3 months ago' },
  { days: 180, value: '6 months ago' },
  { days: 365, value: '1 year ago' },
  { days: 730, value: '2 years ago' },
  { days: 1095, value: '3 years ago' },
];

interface NotConnected {
  days: number;
  value: string;
}

const AdminDevices = () => {
  const history = useHistory();
  const queries = useQuery();
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchForm] = Form.useForm<ReadDevicesRequest>();
  const [moveForm] = Form.useForm<{ stbGroupId: StbGroup['id'] }>();
  const [notDate, setNotDate] = useState<Date | null | string>(null);

  React.useEffect(() => {
    const filterStbGroupId = queries.get('filterStbGroupId');
    if (filterStbGroupId) {
      searchForm.setFieldsValue({ filterStbGroupId: [filterStbGroupId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchForm, queries.get('filterStbGroupId')]);

  const [devices, setDevices] = React.useState<Device[]>([]);
  const [selectedDevices, setSelectedDevices] = React.useState<Device[]>([]);
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: devices.length,
  });
  const { settings, columnsDevice, setColumnsDevice, columnsDeviceEtc, setColumnsDeviceEtc, user } =
    React.useContext(AppContext);
  const [stbGroups, setStbGroups] = useState<Array<StbGroupNameResponse>>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [jobIds, setJobIds] = useState<JobExportDevices['id']>();
  const [orderColumn, setOrderColumn] = useState<string>();
  const [directionSort, setDirectionSort] = useState<ReadDevicesDirectionEnum>();
  const { deviceFilter, setDeviceFilter, setDeviceTagsFilter, deviceTagsFilter } = React.useContext(AppContext);
  const [firmwareVersions, setFirmwareVersions] = useState<DeviceFilterVersions>({
    loaderVersions: [],
    fwVersions: [],
    uiVersions: [],
    customFw1Versions: [],
    customFw2Versions: [],
    customFw3Versions: [],
  });
  React.useEffect(() => {
    if (user) {
      Promise.all([
        api.administration.getDeviceTypes(),
        api.stbGroups.getStbGroupsFilter(),
        api.devices.getLoaderVersions(),
        api.devices.getFirmwareVersions(),
        api.devices.getUiVersions(),
        api.devices.getCFW1Versions(),
        api.devices.getCFW2Versions(),
        api.devices.getCFW3Versions(),
      ])
        .then(
          ([
            newDeviceTypes,
            newStbGroups,
            loaderVersion,
            fwVersion,
            uiVersion,
            cfw1Version,
            cfw2Version,
            cfw3Version,
          ]) => {
            setStbGroups(newStbGroups);
            setDeviceTypes(newDeviceTypes);
            setFirmwareVersions({
              loaderVersions: loaderVersion,
              fwVersions: fwVersion,
              uiVersions: uiVersion,
              customFw1Versions: cfw1Version,
              customFw2Versions: cfw2Version,
              customFw3Versions: cfw3Version,
            });
          },
        )
        .catch((err) => {
          console.error(err);
          toast.error('Devices was not loaded due to error');
        })
        .finally(() => setIsLoading(false));
    }
  }, []);

  const handleSearch = React.useCallback(
    async (newPagination: TablePaginationConfig, column?, order?) => {
      try {
        if (deviceFilter?.length) {
          setIsLoading(true);
          const now = new Date();
          const isoString = now.toISOString();
          const values = await searchForm.validateFields();
          const newDevices = await api.devices.getDevices({
            ...values,
            filterDeviceTypeId: [deviceFilter],
            notConnectedSince: notDate ? new Date(notDate) : new Date(isoString),
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
            orderBy: column || orderColumn,
            direction: order || directionSort,
          });
          setDevices(newDevices.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newDevices.totalElementCount || newDevices.elements.length,
          });
          setDeviceFilter('');
          setDeviceTagsFilter(undefined);
        } else if (deviceTagsFilter?.length) {
          setIsLoading(true);
          const now = new Date();
          const isoString = now.toISOString();
          const values = await searchForm.validateFields();
          const newDevices = await api.devices.getDevices({
            ...values,
            notConnectedSince: notDate ? new Date(notDate) : new Date(isoString),
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
            filterTags: deviceTagsFilter,
            orderBy: column || orderColumn,
            direction: order || directionSort,
          });
          setDevices(newDevices.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newDevices.totalElementCount || newDevices.elements.length,
          });
          setDeviceTagsFilter(undefined);
        } else {
          setIsLoading(true);
          const now = new Date();
          const isoString = now.toISOString();
          const values = await searchForm.validateFields();
          const newDevices = await api.devices.getDevices({
            ...values,
            notConnectedSince: notDate ? new Date(notDate) : new Date(isoString),
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
            orderBy: column || orderColumn,
            direction: order || directionSort,
          });
          setDevices(newDevices.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: newDevices.totalElementCount || newDevices.elements.length,
          });
        }
      } catch (err) {
        const errorObj = err as { errorFields?: any[] };
        if (errorObj.errorFields && errorObj.errorFields.length > 0) {
          console.error('handleSearch ~ err', err);
          toast.error('Devices was not loaded due to error');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [searchForm, notDate],
  );
  const handleMove = async () => {
    try {
      setIsLoading(true);
      const values = await moveForm.validateFields();
      await api.stbGroups.moveDevices(
        values.stbGroupId,
        selectedDevices.map(({ id }) => id),
      );
      toast.success('Device(s) moved successfully');
      handleSearch(pagination);
    } catch (err) {
      setIsLoading(false);
      console.error('handleMove ~ err', err);
      toast.error('Devices was not moved due to error');
    }
  };

  const handleDelete = (device: Device) => {
    Modal.confirm({
      title: 'Are you sure delete this device?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setIsLoading(true);
          await api.devices.deleteDevice(device.id);
          toast.success('Device was deleted successfully');
          handleSearch(pagination);
        } catch (error) {
          setIsLoading(false);
          console.log('Device was not deleted ~ error', error);
          toast.error('Device was not deleted');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const openNotification = React.useCallback((job: JobExportDevices['id']) => {
    notification.open({
      message: 'Export success',
      description: 'Devices export job queued successfully.',
      duration: 0,
      type: 'success',
      top: 20,
      style: {
        display: 'block',
        justifyItems: 'center',
        width: 600,
        borderRadius: 5,
        fontSize: 20,
        fontWeight: 500,
      },
      placement: 'top',
      btn: (
        <Button
          onClick={() => {
            history.push(Navigation.JobInfo.replace(':id', job || ''));
          }}
          size="middle"
          type="primary"
        >
          Go to Job
        </Button>
      ),
    });
  }, []);

  const exportJob = async () => {
    try {
      const now = new Date();
      const isoString = now.toISOString();
      const values = await searchForm.validateFields();
      const jobInd = await api.devices.exportDeviceJob({
        ...values,
        notConnectedSince: notDate ? new Date(notDate) : new Date(isoString),
      });
      toast.success('Devices export job started successfully.');
      setJobIds(jobInd.id);
      openNotification(jobInd.id);
    } catch (error) {
      toast.error('Device isn`t exported to the Job page');
    } finally {
      if (jobIds) {
        openNotification(jobIds);
      }
    }
  };

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
      handleSearch(newPagination);
    },
    [handleSearch],
  );

  React.useEffect(() => {
    handleSearch(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSearchForm = React.useCallback(() => {
    searchForm.resetFields();
    handleSearch(pagination);
  }, [handleSearch, pagination, searchForm]);

  const refreshPage = () => {
    handleSearch(pagination);
  };
  const deviceCount = (pagination.pageSize || 0) * (pagination.current || 0);

  const deviceItem = (deviceCount || 0) - ((pagination.pageSize || 0) - 1);

  const role: UserRequest['role'] | any = getUserRoleByRoles(user?.roles || []);

  const handleDate = (values: NotConnected['value'], notConnected: NotConnected[]) => {
    if (values === null || values === undefined) {
      setNotDate(null);
      return;
    }
    const selectedItem = notConnected.find((item: any) => item?.value === values)?.days;
    if (selectedItem) {
      setNotDate(new Date(new Date().setDate(new Date().getDate() - selectedItem)).toISOString());
    }
  };
  const handleSort = (columnKey: string, order: 'ASC' | 'DESC' | any) => {
    setOrderColumn(columnKey);
    setDirectionSort(order);
    handleSearch(pagination, columnKey, order);
  };

  const handleChange = (value: string[]) => {
    setColumnsDevice(value);
    const newItems = [...(columnsDevice || []), ...(columnsDeviceEtc || [])].filter((item) => !value.includes(item));
    setColumnsDeviceEtc(newItems);
  };

  React.useEffect(() => {
    if (user?.id && columnsDevice) {
      api.administration.updateColumnsDevice(user.id, { columns: columnsDevice });
    }
  }, [columnsDevice]);

  const load = devices.map((item) => item.pendingTags.map((el) => el?.command));

  return (
    <>
      <Collapse>
        <Panel className={styles.collapse} key="1" header="Filter Search">
          <Card style={{ marginBottom: '1rem', border: 'none' }}>
            <Form className={styles.form} form={searchForm} layout="horizontal" name="searchForm">
              <Form.Item name="filterDeviceTypeId" label="Device Type">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {deviceTypes.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.deviceName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterStbGroupId" label="STB Group">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {stbGroups.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterSerialNumber" label="Serial Number">
                <Input />
              </Form.Item>
              <Form.Item name="filterMacAddress" label="Mac address">
                <Input />
              </Form.Item>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr',
                  alignItems: 'center',
                }}
              >
                <span>Not Connected:</span>
                <Select
                  style={{ width: '100%' }}
                  onChange={(e: NotConnected['value']) => handleDate(e, notConnected)}
                  allowClear
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {notConnected.map((item, index) => (
                    <Option key={index} value={item.value}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </div>
              <Form.Item name="filterUiVersion" label="UI">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions?.uiVersions.map((item: any) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterFirmwareVersion" label="FW">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions?.fwVersions.map((item: string) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterLoaderVersion" label="LOADER">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions?.loaderVersions.map((item: string) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterCustomFirmware1" label="CFW1">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions.customFw1Versions.map((item: string) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterCustomFirmware2" label="CFW2">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions?.customFw2Versions.map((item: string) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterCustomFirmware3" label="CFW3">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  {firmwareVersions?.customFw3Versions.map((item: string) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="filterDeviceStatus" label="Last connection status">
                <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                  <Option value="UP_TO_DATE">Up to date</Option>
                  <Option value="PENDING">Pending</Option>
                  <Option value="POSTPONED">Postponed</Option>
                </Select>
              </Form.Item>
              {settings?.enableTags && (
                <Form.Item name="filterTags" label="Tags">
                  <Select mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                    {Object.entries(TagEnum).map((name: any, index) => (
                      <Option key={index} value={name[1]}>
                        {name[0]}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              <Form.Item name="filterComment" label="Comment">
                <Input />
              </Form.Item>
              <Form.Item
                className={styles.filterButtons}
                extra={
                  <Space size={'middle'}>
                    <Button type="primary" onClick={() => handleSearch(pagination)}>
                      Search
                    </Button>
                    <Button type="default" onClick={resetSearchForm}>
                      Reset
                    </Button>
                  </Space>
                }
              ></Form.Item>
            </Form>
          </Card>
        </Panel>
      </Collapse>
      <Card
        title="Move devices"
        extra={
          <Button type="primary" onClick={handleMove}>
            Move
          </Button>
        }
        style={{ margin: '1rem 0' }}
      >
        <Form form={moveForm} layout="horizontal" name="moveForm">
          <Form.Item
            name="stbGroupId"
            label="Move selected devices to the group"
            tooltip="Select devices to move"
            rules={[
              {
                required: true,
                message: 'Please select the Stb Group',
              },
            ]}
          >
            <Select disabled={!selectedDevices.length} allowClear showSearch filterOption={selectSearchFilter}>
              {stbGroups.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Card>
      <Card
        title={`My Devices: ${deviceItem}-${deviceCount} / ${pagination.total}`}
        bodyStyle={{ padding: 0 }}
        extra={
          <Space>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', minWidth: '300px', maxWidth: '700px' }}
              placeholder="Please Select Columns"
              filterOption={selectSearchFilter}
              value={columnsDevice}
              onChange={handleChange}
              maxTagCount={2}
            >
              {columnsDevice?.map((item, index) => (
                <Option key={item}>{item}</Option>
              ))}
              {columnsDeviceEtc?.map((item, index) => (
                <Option key={item}>{item}</Option>
              ))}
            </Select>
            <Button loading={isLoading} icon={<RedoOutlined />} type="primary" onClick={() => refreshPage()}>
              Refresh
            </Button>
            <Button type="default" onClick={() => exportJob()}>
              Export
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={devices}
          pagination={pagination}
          onChange={handleTableChange}
          rowSelection={{
            type: 'checkbox',
            onChange: (selectedRowKeys, selectedDevices) => {
              setSelectedDevices(selectedDevices);
            },
          }}
          loading={isLoading}
          rowKey={(record: Device) => record.id}
          scroll={{ x: 1200 }}
        >
          {columnsDevice?.includes('Connection date') && (
            <Column
              title="Connection date"
              width="130px"
              dataIndex="lastConnectionDate"
              render={(lastConnectionDate: Device['lastConnectionDate']) => {
                const formatDate = lastConnectionDate?.toLocaleString();
                const dateLines = formatDate?.split(',') || [];
                const [date = '', time = ''] = dateLines;
                return (
                  <div>
                    <div>{date}</div>
                    <div>{time}</div>
                  </div>
                );
              }}
            />
          )}
          {columnsDevice?.includes('Serial Number') && (
            <Column
              title="Serial Number"
              dataIndex="serialNumber"
              render={(serialNumber: Device['serialNumber']) => serialNumber}
            />
          )}
          {columnsDevice?.includes('MAC address') && (
            <Column
              title="MAC address"
              dataIndex="macAddress"
              render={(macAddress: Device['macAddress']) => macAddress}
            />
          )}
          {columnsDevice?.includes('Device Type') && (
            <Column title="Device Type" dataIndex="etc" render={(etc: Device['etc']) => etc.deviceTypeName} />
          )}
          {columnsDevice?.includes('LOADER Version') && (
            <Column title={settings?.loaderLabelName || 'LOADER Version'} dataIndex="loaderVersion" />
          )}
          {columnsDevice?.includes('FW Version') && (
            <Column title={settings?.firmwareLabelName || 'FW Version'} dataIndex="firmwareVersion" />
          )}
          {columnsDevice?.includes('UI Version') && (
            <Column title={settings?.uiLabelName || 'UI Version'} dataIndex="uiVersion" />
          )}
          {columnsDevice?.includes('Loader tries') && (
            <Column
              title="LOADER Tries"
              dataIndex="loaderCumulativeCount"
              sorter={true}
              onHeaderCell={() => ({
                onClick: () => {
                  if (orderColumn === 'loaderCumulativeCount' && directionSort === 'ASC') {
                    handleSort('loaderCumulativeCount', 'DESC');
                  } else if (orderColumn === 'loaderCumulativeCount' && directionSort === 'DESC') {
                    handleSort('', null);
                  } else {
                    handleSort('loaderCumulativeCount', 'ASC');
                  }
                },
              })}
            />
          )}
          {columnsDevice?.includes('Fw tries') && (
            <Column
              title="FW Tries"
              dataIndex="fwCumulativeCount"
              sorter={true}
              onHeaderCell={() => ({
                onClick: () => {
                  if (orderColumn === 'fwCumulativeCount' && directionSort === 'ASC') {
                    handleSort('fwCumulativeCount', 'DESC');
                  } else if (orderColumn === 'fwCumulativeCount' && directionSort === 'DESC') {
                    handleSort('', null);
                  } else {
                    handleSort('fwCumulativeCount', 'ASC');
                  }
                },
              })}
            />
          )}
          {columnsDevice?.includes('Ui tries') && (
            <Column
              title="UI Tries"
              dataIndex="uiCumulativeCount"
              sorter={true}
              onHeaderCell={() => ({
                onClick: () => {
                  if (orderColumn === 'uiCumulativeCount' && directionSort === 'ASC') {
                    handleSort('uiCumulativeCount', 'DESC');
                  } else if (orderColumn === 'uiCumulativeCount' && directionSort === 'DESC') {
                    handleSort('', null);
                  } else {
                    handleSort('uiCumulativeCount', 'ASC');
                  }
                },
              })}
            />
          )}
          {columnsDevice?.includes('Group') && (
            <Column title="Group" dataIndex="etc" render={(etc: Device['etc']) => etc.groupName} />
          )}
          {columnsDevice?.includes('Comment') && <Column title="Comment" dataIndex="comment" />}
          {columnsDevice?.includes('Last connection status') && (
            <Column
              title="Last connection status"
              dataIndex="lastConnectionStatus"
              render={(text, status: Device) => {
                return !status.isSyncWithUpgradeRule ? (
                  <Tooltip title={'Rule updated since last device connection'}>
                    <div style={{ display: 'grid', justifyContent: 'center', justifyItems: 'center' }}>
                      <Tag color="gold">!</Tag>
                      {status.lastConnectionStatus}
                    </div>
                  </Tooltip>
                ) : (
                  <span>{status.lastConnectionStatus}</span>
                );
              }}
            />
          )}
          {columnsDevice?.includes('CFW1') && (
            <Column
              title={settings?.customFirmware1LabelName || 'CFW1'}
              dataIndex="customFirmware1Version"
              render={(etc: Device['customFirmware1Version']) => etc}
            />
          )}
          {columnsDevice?.includes('CFW2') && (
            <Column
              title={settings?.customFirmware2LabelName || 'CFW2'}
              dataIndex="customFirmware2Version"
              render={(etc: Device['customFirmware2Version']) => etc}
            />
          )}
          {columnsDevice?.includes('CFW3') && (
            <Column
              title={settings?.customFirmware3LabelName || 'CFW3'}
              dataIndex="customFirmware3Version"
              render={(etc: Device['customFirmware3Version']) => etc}
            />
          )}

          {columnsDevice?.includes('Tags') && settings?.enableTags && (
            <Column
              width="130px"
              title="Tags"
              dataIndex="pendingTags"
              align="center"
              render={(pendingTags, locked: Device) => {
                return (
                  <div>
                    {pendingTags?.map((item: any) => (
                      <Tag key={item.id}>{item.command}</Tag>
                    ))}
                    <Tag>{locked.isLocked ? 'Locked' : 'Unlocked'}</Tag>
                  </div>
                );
              }}
            />
          )}
          {columnsDevice?.includes('Attributes') && (
            <Column
              title="Attributes"
              align="center"
              render={(_, r: Device) => (
                <>
                  {settings?.enableStbCommands && r.isFullResetAttributeSet && (
                    <Popover content="Full Reset">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2.5em',
                          height: '2.5em',
                          borderRadius: '50%',
                        }}
                        type="primary"
                      >
                        FR
                      </Button>
                    </Popover>
                  )}
                  {settings?.enableStbCommands && r.isLightResetAttributeSet && (
                    <Popover content="Light Reset">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2.5em',
                          height: '2.5em',
                          borderRadius: '50%',
                        }}
                        type="primary"
                      >
                        LR
                      </Button>
                    </Popover>
                  )}
                  {settings?.enableStbCommands && r.isTraceEnabledAttributeSet && (
                    <Popover content="Trace Enable">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2.5em',
                          height: '2.5em',
                          borderRadius: '50%',
                        }}
                        type="primary"
                      >
                        TE
                      </Button>
                    </Popover>
                  )}
                  {settings?.enableDeviceHistory && r.isLogHistoryAttributeSet && (
                    <Popover content="Log History">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2.5em',
                          height: '2.5em',
                          borderRadius: '50%',
                        }}
                        type="primary"
                      >
                        LH
                      </Button>
                    </Popover>
                  )}
                  {r.isMoveToPreproductionAttributeSet && (
                    <Popover content="Move to Preprod">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2.5em',
                          height: '2.5em',
                          borderRadius: '50%',
                        }}
                        type="primary"
                      >
                        MTP
                      </Button>
                    </Popover>
                  )}
                </>
              )}
            />
          )}
          <Column
            width="100px"
            align="center"
            fixed="right"
            title="Actions"
            key="action"
            render={(_text, device: Device) => (
              <Space size="middle">
                <InfoCircleOutlined
                  onClick={() =>
                    history.push(
                      Navigation.ReportsById.replace(':tab', 'sn-mac')
                        .replace(':type?', 'MAC')
                        .replace(':value?', encodeURIComponent(device.macAddress || '')),
                    )
                  }
                />
                {role && Role.Quality !== role && (
                  <>
                    <FormOutlined onClick={() => history.push(Navigation.UpdateDevice.replace(':id', device.id))} />
                    <DeleteOutlined onClick={() => handleDelete(device)} />
                  </>
                )}
              </Space>
            )}
          />
        </Table>
      </Card>
    </>
  );
};
export default React.memo(AdminDevices);
